import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  crewLookupApiBase,
  crewLookupListPath,
  crewLookupWorkspacePath,
  type CrewLookupDef} from './registry';
import type { CrewLookupItem } from './useData';

function buildExtraFields(def: CrewLookupDef): FieldConfig[] {
  const extraFields: FieldConfig[] = [];
  if (def.extras.includes('abrv')) {
    extraFields.push({
      name: 'abrv',
      label: 'Abbreviation',
      type: 'text',
      placeholder: 'e.g. C/O, Master',
      helperText: 'Short form shown in crew lists and documents.',
      validation: { maxLength: 10 }});
  }
  if (def.extras.includes('phone')) {
    extraFields.push({
      name: 'phone',
      label: 'Phone',
      type: 'text',
      placeholder: 'e.g. +30 210 0000000',
      validation: { maxLength: 50 }});
  }
  if (def.extras.includes('email')) {
    extraFields.push({
      name: 'email',
      label: 'Email',
      type: 'text',
      placeholder: 'e.g. agent@example.com',
      validation: { maxLength: 200 }});
  }
  if (def.extras.includes('description')) {
    extraFields.push({
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Optional notes for this entry',
      validation: { maxLength: 250 }});
  }
  return extraFields;
}

function buildFormLayout(def: CrewLookupDef, extraFieldNames: string[]): FormLayout {
  const catalogExtras = def.kind === 'cert-catalogs' ? ['categoryId'] : [];
  const rankExtras = def.kind === 'ranks' ? ['groupType'] : [];
  const identityFields = ['code', 'name', ...catalogExtras, ...rankExtras, ...extraFieldNames];
  const identitySpans: Record<string, string> = {
    code: 'md:col-span-4',
    name: 'md:col-span-8',
    categoryId: 'md:col-span-6',
    groupType: 'md:col-span-4',
    abrv: 'md:col-span-4',
    phone: 'md:col-span-6',
    email: 'md:col-span-6',
    description: 'md:col-span-12'};

  // Keep abbreviation on the same row as name when present by adjusting spans.
  if (def.extras.includes('abrv')) {
    identitySpans.code = 'md:col-span-3';
    identitySpans.name = 'md:col-span-5';
    identitySpans.abrv = 'md:col-span-2';
    if (def.kind === 'ranks') {
      identitySpans.name = 'md:col-span-4';
      identitySpans.abrv = 'md:col-span-2';
      identitySpans.groupType = 'md:col-span-3';
    }
  }

  if (def.kind === 'cert-catalogs') {
    identitySpans.code = 'md:col-span-3';
    identitySpans.name = 'md:col-span-5';
    identitySpans.categoryId = 'md:col-span-4';
  }

  return {
    mode: 'default',
    sections: [
      {
        id: 'identification',
        title: 'Identification',
        description: `Code and name for this ${def.singular.toLowerCase()}.`,
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: identityFields,
        fieldSpans: identitySpans,
        collapsible: true,
        defaultCollapsed: false},
      {
        id: 'display',
        title: 'Display & status',
        description: 'Ordering in pickers and whether this value can be assigned.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['sortOrder', 'isActive'],
        fieldSpans: {
          sortOrder: 'md:col-span-4',
          isActive: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: false},
    ]};
}

export type CrewLookupRouteOptions = {
  /** List and cancel destination. */
  list: string;
  /** Prefix for /new, /:id, and /:id/edit. */
  recordBase: string;
  permissionModuleKey?: string;
};

export function buildCrewLookupModuleConfig(def: CrewLookupDef, routes?: CrewLookupRouteOptions) {
  const listPath = routes?.recordBase ?? crewLookupListPath(def.kind);
  const workspacePath = routes?.list ?? crewLookupWorkspacePath(def.kind);
  const apiBase = crewLookupApiBase(def.kind);
  const lower = def.singular.toLowerCase();
  const extraFields = buildExtraFields(def);
  const extraFieldNames = extraFields.map((f) => f.name);
  const catalogCategoryField: FieldConfig[] =
    def.kind === 'cert-catalogs'
      ? [
          {
            name: 'categoryId',
            label: 'Category',
            type: 'select',
            placeholder: 'Select category',
            apiEndpoint: '/crew-lookups/cert-categories?page=1&limit=500',
            valueField: 'id',
            labelField: 'name',
            options: [{ value: '', label: 'No category' }],
            helperText: 'Optional group for this catalog entry.'},
        ]
      : [];
  const rankGroupField: FieldConfig[] =
    def.kind === 'ranks'
      ? [
          {
            name: 'groupType',
            label: 'Group',
            type: 'select',
            placeholder: 'Select group…',
            options: [
              { value: '', label: 'Not set' },
              { value: '1', label: 'Officers' },
              { value: '2', label: 'Ratings' },
            ],
            helperText: 'Officers (1) or Ratings (2) — used for matrices and manning filters.',
            showClearButton: false},
        ]
      : [];

  return {
    module: {
      title: def.label,
      permissionModuleKey: routes?.permissionModuleKey ?? 'crew_parameters',
      route: {
        list: workspacePath,
        new: `${listPath}/new`,
        detail: (id: number) => `${listPath}/${id}`,
        edit: (id: number) => `${listPath}/${id}/edit`},
      api: {
        list: apiBase,
        detail: (id: number) => `${apiBase}/${id}`}},
    pagination: {
      initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
      itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
    search: { placeholder: `Search ${def.label.toLowerCase()} by code or name…` },
    filterGroups: [
      {
        name: 'basic',
        label: 'Basic',
        icon: '',
        fields: ['name', 'isActive']},
    ],
    filters: {
      fields: [
        {
          key: 'name',
          label: 'Name',
          placeholder: 'All names',
          getValue: (item: CrewLookupItem) => item.name,
          options: []},
        {
          key: 'isActive',
          label: 'Active',
          type: 'boolean' as const,
          getValue: (item: CrewLookupItem) => (item.isActive ? 'Yes' : 'No'),
          options: [
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ]},
      ],
      applyFilter: (items: CrewLookupItem[], filters: Record<string, string[]>) => {
        return items.filter((item) => {
          if (filters.name?.length) {
            const v = item.name || '';
            if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
          }
          if (filters.isActive?.length) {
            const s = item.isActive ? 'Yes' : 'No';
            if (!filters.isActive.includes(s)) return false;
          }
          return true;
        });
      }},
    toolbar: {
      actions: [
        {
          key: 'add',
          icon: 'add',
          title: `New ${lower}`,
          showTitle: false,
          variant: 'primary' as const,
          href: `${listPath}/new`},
      ]},
    table: {
      actionsPosition: 'left' as const,
      keyExtractor: (item: CrewLookupItem) => item.id,
  },
    emptyStates: {
      noData: `Add a ${lower} to use in crew records.`,
      noSearchResults: `No ${def.label.toLowerCase()} match your search.`,
      noFilterResults: `No ${def.label.toLowerCase()} match the filters.`},
    delete: {
      title: `Delete ${lower}`,
      message: `Delete this ${lower}? Crew records that reference it may be affected.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      errorMessage: `Could not delete ${lower}. It may be in use.`,
      handler: async (id: number) => {
        const { deleteCrewLookup } = await import('../../../services/mutations');
        await deleteCrewLookup(def.kind, id);
      }},
    utils: {
      formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
    detail: {
      subtitle: def.singular,
      notFoundTitle: 'Not found',
      notFoundMessage: `This ${lower} does not exist.`,
      notFoundBackText: 'Back to list',
      defaultTitle: def.singular,
      getTitle: (item: CrewLookupItem) =>
        item.name || `${def.singular} #${item.id}`},
    form: {
      new: {
        title: `New ${lower}`,
        subtitle: `Add a ${lower} for crew reference data`,
        submitText: 'Create'},
      edit: {
        title: `Edit ${lower}`,
        subtitle: `Update identification, ordering, and availability`,
        submitText: 'Save'},
      cancelText: 'Cancel',
      loadingText: 'Loading...',
      errorMessage: 'Save failed.',
      formSizing: 'content' as const,
      formMaxWidth: 'max-w-3xl',
      create: async (data: Partial<CrewLookupItem>) => {
        const { createCrewLookup } = await import('../../../services/mutations');
        return createCrewLookup(def.kind, data as Record<string, unknown>);
      },
      update: async (id: number, data: Partial<CrewLookupItem>) => {
        const { updateCrewLookup } = await import('../../../services/mutations');
        return updateCrewLookup(def.kind, id, data as Record<string, unknown>);
      }},
    fields: [
      {
        name: 'code',
        label: 'Code',
        type: 'text',
        placeholder: 'e.g. CO, MASTER',
        helperText: 'Optional short code for imports and integrations.',
        validation: { maxLength: 20 }},
      {
        name: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: `e.g. ${def.singular}`,
        helperText: 'Display name shown in crew forms and lists.',
        validation: { required: true, minLength: 1, maxLength: 200 }},
      ...catalogCategoryField,
      ...rankGroupField,
      ...extraFields,
      {
        name: 'sortOrder',
        label: 'Sort order',
        type: 'number',
        placeholder: '0',
        helperText: 'Lower numbers appear first in selection lists.',
        defaultValue: 0,
        validation: { min: 0 }},
      {
        name: 'isActive',
        label: 'Active',
        type: 'switch',
        switchLabel: `Available for new crew records`,
        description: `Inactive ${def.label.toLowerCase()} stay on existing records but are hidden from new assignments.`,
        defaultValue: true},
    ] as FieldConfig[],
    formLayout: buildFormLayout(def, extraFieldNames)};
}

const TextCell: React.FC<{ value: string | null | undefined; className?: string }> = ({ value, className }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text, className)}>{value || ''}</span>;
};

export const getColumns = (
  def: CrewLookupDef,
  formatDate: (dateString?: string) => string,
): Column<CrewLookupItem>[] => {
  const cols: Column<CrewLookupItem>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (v) => <TextCell value={v} className="font-mono text-xs tracking-wide" />},
    { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  ];
  if (def.kind === 'cert-catalogs') {
    cols.push({
      key: 'categoryId',
      header: 'Category',
      sortable: true,
      render: (_v, row) => <TextCell value={row?.category?.name} />});
  }
  if (def.extras.includes('abrv')) {
    cols.push({
      key: 'abrv',
      header: 'Abrv',
      sortable: true,
      render: (v) => <TextCell value={v} className="font-mono text-xs" />});
  }
  if (def.kind === 'ranks') {
    cols.push({
      key: 'groupType',
      header: 'Group',
      sortable: true,
      render: (v) => (
        <TextCell value={v === 1 || v === '1' ? 'Officers' : v === 2 || v === '2' ? 'Ratings' : undefined} />
      )});
  }
  if (def.extras.includes('phone')) {
    cols.push({ key: 'phone', header: 'Phone', sortable: true, render: (v) => <TextCell value={v} /> });
  }
  if (def.extras.includes('email')) {
    cols.push({ key: 'email', header: 'Email', sortable: true, render: (v) => <TextCell value={v} /> });
  }
  cols.push(
    {
      key: 'isActive',
      header: 'Active',
      sortable: true,
      render: (v, row) => (
        <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
          {v ?? row?.isActive ? 'Yes' : 'No'}
        </Badge>
      )},
    { key: 'sortOrder', header: 'Order', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
    { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
  );
  return cols;
};

export const getDetailSections = (def: CrewLookupDef, item: CrewLookupItem, formatDate: (s?: string) => string) => {
  const identity: Array<{ label: string; value: string }> = [
    { label: 'Code', value: item.code || '-' },
    { label: 'Name', value: item.name },
  ];
  if (def.extras.includes('abrv')) identity.push({ label: 'Abbreviation', value: item.abrv || '-' });
  if (def.kind === 'ranks') {
    identity.push({
      label: 'Group',
      value: item.groupType === 1 ? 'Officers' : item.groupType === 2 ? 'Ratings' : '-'});
  }
  if (def.kind === 'cert-catalogs') {
    identity.push({ label: 'Category', value: item.category?.name || '-' });
  }
  if (def.extras.includes('phone')) identity.push({ label: 'Phone', value: item.phone || '-' });
  if (def.extras.includes('email')) identity.push({ label: 'Email', value: item.email || '-' });
  if (def.extras.includes('description')) identity.push({ label: 'Description', value: item.description || '-' });

  return [
    { title: 'Identification', fields: identity },
    {
      title: 'Display & status',
      fields: [
        { label: 'Sort order', value: String(item.sortOrder ?? 0) },
        { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
        { label: 'Created', value: formatDate(item.createdAt) },
        { label: 'Updated', value: formatDate(item.updatedAt) },
      ]},
  ];
};

export const getInitialFormData = (def: CrewLookupDef): Partial<CrewLookupItem> => {
  const base: Partial<CrewLookupItem> = {
    code: '',
    name: '',
    sortOrder: 0,
    isActive: true};
  if (def.extras.includes('abrv')) base.abrv = '';
  if (def.extras.includes('phone')) base.phone = '';
  if (def.extras.includes('email')) base.email = '';
  if (def.extras.includes('description')) base.description = '';
  if (def.kind === 'cert-catalogs') base.categoryId = undefined;
  if (def.kind === 'ranks') base.groupType = undefined;
  return base;
};

export const mapItemToFormData = (item: CrewLookupItem): Partial<CrewLookupItem> => ({
  code: item.code ?? '',
  name: item.name,
  abrv: item.abrv ?? '',
  phone: item.phone ?? '',
  email: item.email ?? '',
  description: item.description ?? '',
  categoryId: item.categoryId ?? undefined,
  groupType: item.groupType ?? undefined,
  sortOrder: item.sortOrder,
  isActive: item.isActive});

export const getTableActions = (
  def: CrewLookupDef,
  item: CrewLookupItem,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  const listPath = crewLookupListPath(def.kind);
  return (
    <div className="flex items-center flex-nowrap">
      <Link
        to={`${listPath}/${item.id}`}
        className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={`${listPath}/${item.id}/edit`}
          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;
